import * as THREE from 'three';
import { snapDegrees } from '../setup/locomotion';
import {
  DEG_TO_RAD,
  HAND,
  LOCO_CONT_TURN_SPEED_DEG,
  LOCO_DEAD_ZONE,
  LOCO_MOVE_SPEED_MS,
  LOCO_SNAP_RELEASE,
  LOCO_SNAP_TRIGGER,
  TURN_MODE,
  TURN_SNAP,
  type TurnMode,
  type TurnSnap,
} from '../config/constants';

export type TurnSnapState = { lastSnap: TurnSnap };

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _camQuat = new THREE.Quaternion();
const _camBefore = new THREE.Vector3();
const _camAfter = new THREE.Vector3();

/** Reads the active thumbstick axes from a controller's gamepad. */
function readThumbstick(gp: Gamepad, dead: number): { x: number; y: number } {
  // xr-standard on Quest exposes the stick at axes[2,3] (axes[0,1] = unused touchpad).
  // Other runtimes report it at [0,1]: pick whichever pair has signal.
  const x2 = gp.axes[2] ?? 0;
  const y2 = gp.axes[3] ?? 0;
  const x0 = gp.axes[0] ?? 0;
  const y0 = gp.axes[1] ?? 0;
  const useSecondary = Math.hypot(x2, y2) >= Math.hypot(x0, y0);
  const ax = useSecondary ? x2 : x0;
  const ay = useSecondary ? y2 : y0;
  return {
    x: Math.abs(ax) > dead ? ax : 0,
    y: Math.abs(ay) > dead ? ay : 0,
  };
}

/** Rotate the rig around the camera's world position (yaw "in place"). */
function rotatePlayer(rig: THREE.Group, cam: THREE.Camera, deltaRad: number): void {
  cam.getWorldPosition(_camBefore);
  rig.rotation.y += deltaRad;
  rig.updateMatrixWorld(true);
  cam.getWorldPosition(_camAfter);
  rig.position.x += _camBefore.x - _camAfter.x;
  rig.position.z += _camBefore.z - _camAfter.z;
  rig.updateMatrixWorld(true);
}

/** Left thumbstick → translation; right thumbstick Y → forward/back. */
export function tickLocomotion(
  session: XRSession,
  rig: THREE.Group,
  cam: THREE.Camera,
  dt: number,
): void {
  let mx = 0;
  let mz = 0;
  for (const src of session.inputSources) {
    const gp = src.gamepad;
    if (!gp) continue;
    if (src.handedness === HAND.left) {
      const a = readThumbstick(gp, LOCO_DEAD_ZONE);
      mx += a.x;
      mz += a.y;
    } else if (src.handedness === HAND.right) {
      const a = readThumbstick(gp, LOCO_DEAD_ZONE);
      mz += a.y;
    }
  }
  if (mx === 0 && mz === 0) return;

  // World orientation, not cam.quaternion: the camera is parented to the rig, so its local
  // quaternion excludes the rig's yaw (from snap/continuous turning). Using it would offset
  // movement by the accumulated turn angle.
  cam.getWorldQuaternion(_camQuat);

  _forward.set(0, 0, -1).applyQuaternion(_camQuat);
  _forward.y = 0;
  _forward.normalize();

  _right.set(1, 0, 0).applyQuaternion(_camQuat);
  _right.y = 0;
  _right.normalize();

  rig.position.addScaledVector(_forward, -mz * LOCO_MOVE_SPEED_MS * dt);
  rig.position.addScaledVector(_right, mx * LOCO_MOVE_SPEED_MS * dt);
}

/** Right thumbstick X → yaw (snap or continuous, per turnMode). */
export function tickRotation(
  session: XRSession,
  rig: THREE.Group,
  cam: THREE.Camera,
  dt: number,
  mode: TurnMode,
  state: TurnSnapState,
): void {
  let x = 0;
  for (const src of session.inputSources) {
    if (src.handedness !== HAND.right) continue;
    const gp = src.gamepad;
    if (!gp) break;
    x = readThumbstick(gp, 0).x;
    break;
  }

  if (mode === TURN_MODE.continuous) {
    const m = Math.abs(x) > LOCO_DEAD_ZONE ? x : 0;
    if (m === 0) return;
    rotatePlayer(rig, cam, -m * LOCO_CONT_TURN_SPEED_DEG * DEG_TO_RAD * dt);
    return;
  }

  const step = snapDegrees(mode) * DEG_TO_RAD;
  if (Math.abs(x) < LOCO_SNAP_RELEASE) {
    state.lastSnap = TURN_SNAP.neutral;
    return;
  }
  if (x > LOCO_SNAP_TRIGGER && state.lastSnap !== TURN_SNAP.right) {
    rotatePlayer(rig, cam, -step);
    state.lastSnap = TURN_SNAP.right;
  } else if (x < -LOCO_SNAP_TRIGGER && state.lastSnap !== TURN_SNAP.left) {
    rotatePlayer(rig, cam, step);
    state.lastSnap = TURN_SNAP.left;
  }
}
